/**
 * Корзина складских операций — текущий формирующийся документ.
 *
 * Логика как в Excel-системе «Складской учет 20»:
 * - Кладовщик ходит по таблице остатков, кнопками + / − добавляет в корзину позицию
 *   с положительным (приход) или отрицательным (расход) количеством.
 * - В шапке всегда видно сколько позиций / на какую сумму.
 * - Из drawer'а можно сохранить как накладную (inflow / outflow) или очистить.
 *
 * Хранится в localStorage чтобы не потерять при случайном F5.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from "react";


export interface CartItem {
  item_id: number;
  sku: string | null;
  name: string;
  unit: string | null;
  warehouse: string;
  quantity: number;        // положительное = приход, отрицательное = расход
  price: number | null;    // цена закупа (для прихода) или розничная (для расхода) — выбирается при сохранении
  note?: string;
}

interface CartCtx {
  items: CartItem[];
  add: (it: CartItem) => void;
  setQty: (item_id: number, delta: number) => void;
  remove: (item_id: number) => void;
  clear: () => void;
  count: number;
  total: number;
}

const KEY = "kb.warehouse.cart.v1";
const Context = createContext<CartCtx | null>(null);


function load(): CartItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}
function save(items: CartItem[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch {}
}


export function WarehouseCartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => load());

  useEffect(() => { save(items); }, [items]);

  const add = useCallback((it: CartItem) => {
    setItems(prev => {
      const idx = prev.findIndex(x => x.item_id === it.item_id);
      if (idx === -1) return [...prev, it];
      const updated = [...prev];
      updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + it.quantity };
      // если стало 0 — удаляем
      if (Math.abs(updated[idx].quantity) < 1e-9) {
        updated.splice(idx, 1);
      }
      return updated;
    });
  }, []);

  const setQty = useCallback((item_id: number, delta: number) => {
    setItems(prev => {
      const idx = prev.findIndex(x => x.item_id === item_id);
      if (idx === -1) return prev;
      const updated = [...prev];
      const next = updated[idx].quantity + delta;
      if (Math.abs(next) < 1e-9) {
        updated.splice(idx, 1);
      } else {
        updated[idx] = { ...updated[idx], quantity: next };
      }
      return updated;
    });
  }, []);

  const remove = useCallback((item_id: number) => {
    setItems(prev => prev.filter(x => x.item_id !== item_id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const { count, total } = useMemo(() => {
    let total = 0;
    for (const it of items) total += Math.abs(it.quantity) * (it.price ?? 0);
    return { count: items.length, total };
  }, [items]);

  const value: CartCtx = { items, add, setQty, remove, clear, count, total };
  return <Context.Provider value={value}>{children}</Context.Provider>;
}


export function useWarehouseCart(): CartCtx {
  const ctx = useContext(Context);
  if (!ctx) throw new Error("useWarehouseCart must be used within WarehouseCartProvider");
  return ctx;
}
